import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import HuntHeader from '../../components/huntHeader/HuntHeader';
import ReactMapGL, {
  MapboxGL,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl,
  Marker,
  Popup,
  Source,
  Layer,
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import ClueCard from '../../components/clueCard/ClueCard';
import ModalImage from 'react-modal-image';
import PlayerAvatars from '../../components/playerAvatars/PlayerAvatars';
import BadgeList from '../../components/badgeList/BadgeList';
import HuntJoinLeaveButton from '../../components/huntJoinLeaveButton/HuntJoinLeaveButton';
import HuntProgressDisplay from '../../components/huntProgressDisplay/HuntProgressDisplay';
import './huntDetailPage.css';
dayjs.extend(relativeTime);
const MAPBOX_TILE_SIZE = 512; // Mapbox GL default tile size
const EARTH_CIRCUMFERENCE = 40075016.686; // in meters

function getBoundingCircle(coords) {
  // Simple: use bounding box center and radius as approximation
  const lats = coords.map((c) => c[1]);
  const lngs = coords.map((c) => c[0]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  // Approximate radius in meters (Haversine formula for max distance from center)
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  let maxDist = 0;
  coords.forEach(([lng, lat]) => {
    const dLat = toRad(lat - center[1]);
    const dLng = toRad(lng - center[0]);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(center[1])) *
        Math.cos(toRad(lat)) *
        Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    if (d > maxDist) maxDist = d;
  });
  return { center, radius: maxDist };
}

const circleLayer = {
  id: 'clue-circle',
  type: 'fill',
  paint: {
    'fill-color': '#3b82f6',
    'fill-opacity': 0.1,
  },
};

const circleOutline = {
  id: 'clue-circle-outline',
  type: 'line',
  paint: {
    'line-color': '#2563eb',
    'line-width': 2,
  },
};

const HuntDetailPage = () => {
  const { huntId } = useParams();
  const { player } = useContext(AuthContext);
  const [hunt, setHunt] = useState(null);
  const [clues, setClues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewport, setViewport] = useState({
    latitude: 36.3,
    longitude: -88.3,
    zoom: 12,
  });
  const [selectedClue, setSelectedClue] = useState(null);
  const [circleGeoJSON, setCircleGeoJSON] = useState(null);
  const [isJoined, setIsJoined] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [unlockedClues, setUnlockedClues] = useState([]);
  const mapRef = useRef(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await axiosConn.get(`hunt-progress/${huntId}`, {
          headers: { Authorization: `Bearer ${player.token}` },
        });
        setUnlockedClues(res.data.data.data.cluesUnlocked); // Array of clue IDs
      } catch (err) {
        if (err.response && err.response.status === 404) {
          // No progress yet, treat as empty
          setUnlockedClues([]);
        } else {
          // Optionally handle other errors
          console.error('Error fetching hunt progress:', err);
        }
      }
    };
    fetchProgress();
  }, [huntId]);

  useEffect(() => {
    const fetchHuntAndClues = async () => {
      try {
        const huntRes = await axiosConn.get(`hunts/${huntId}`, {
          headers: {
            Authorization: `Bearer ${player?.token}`,
          },
        });
        // console.log('Hunt data:', huntRes.data.data);
        setHunt(huntRes.data.data);

        // Check if player has already joined this hunt
        const joined = huntRes.data.data.players?.some(
          (p) =>
            (typeof p === 'object' ? p._id : p.toString()) ===
            player.data.player._id,
        );
        setIsJoined(joined);

        const clueRes = await axiosConn.get(`clues/hunt/${huntId}`, {
          headers: {
            Authorization: `Bearer ${player?.token}`,
          },
        });
        // console.log('Clues data:', clueRes.data);
        setClues(clueRes.data);

        // Calculate bounding circle if clues exist
        const coords = clueRes.data
          .filter(
            (clue) =>
              Array.isArray(clue.location.coordinates) &&
              clue.location.coordinates.length === 2,
          )
          .map((clue) => clue.location.coordinates);

        if (coords.length > 0) {
          const { center, radius } = getBoundingCircle(coords);

          // Create a GeoJSON circle (approximate with 64 points)
          const points = 64;
          const circleCoords = [];
          for (let i = 0; i < points; i++) {
            const angle = (i / points) * 2 * Math.PI;
            // Offset in meters
            const dx = radius * Math.cos(angle);
            const dy = radius * Math.sin(angle);
            // Convert meters to degrees
            const deltaLng =
              dx / (111320 * Math.cos((center[1] * Math.PI) / 180));
            const deltaLat = dy / 110540;
            circleCoords.push([center[0] + deltaLng, center[1] + deltaLat]);
          }
          circleCoords.push(circleCoords[0]); // close the polygon

          setCircleGeoJSON({
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [circleCoords],
                },
              },
            ],
          });

          // Calculate the diameter of the circle in meters
          const diameter = radius * 2;

          // Calculate the map's pixel width (85vw)
          const mapPxWidth = window.innerWidth * 0.85;

          // Calculate zoom so the diameter fits 70% of the map width (for padding)
          const zoom = Math.log2(
            (mapPxWidth * EARTH_CIRCUMFERENCE) /
              (MAPBOX_TILE_SIZE * diameter * 3),
          );

          // Set viewport to fit the circle
          setViewport((v) => ({
            ...v,
            latitude: center[1],
            longitude: center[0],
            zoom,
          }));
        }
      } catch (error) {
        console.error('Error loading treasure hunt or clues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHuntAndClues();
  }, [huntId, player?.token]);

  if (loading) {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p>Loading treasure hunt details...</p>
      </div>
    );
  }

  if (!hunt) {
    return (
      <div className="container mt-5 text-center">
        <p>Treasure hunt not found or not available.</p>
      </div>
    );
  }

  const handleJoin = async () => {
    try {
      setButtonLoading(true);

      console.log('Player jgkTokens:', player.data.player.jgkTokens);
      // Check balance first
      if (player.data.player.jgkTokens >= hunt.priceInJGK) {
        await axiosConn.post(
          `/hunts/${huntId}/join`,
          {},
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        setIsJoined(true);
      } else {
        // Redirect to Stripe checkout session if not enough JGK
        const res = await axiosConn.get(
          `/huntBookings/checkout-session/${huntId}`,
          // {
          //   // huntId: huntId,
          //   // playerId: player.data.player._id,
          // },
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        window.location.href = res.data.session.url;
      }
    } catch (err) {
      console.error('Failed to join hunt:', err);
    } finally {
      setButtonLoading(false);
    }
  };

  const handleLeave = async () => {
    try {
      setButtonLoading(true);
      await axiosConn.post(
        `/hunts/${huntId}/leave`,
        {},
        {
          headers: {
            Authorization: `Bearer ${player?.token}`,
          },
        },
      );
      setIsJoined(false);
    } catch (err) {
      console.error('Failed to leave hunt:', err);
    } finally {
      setButtonLoading(false);
    }
  };

  const fetchUnlockedClues = async () => {
    try {
      const res = await axiosConn.get(`hunt-progress/${huntId}`, {
        headers: { Authorization: `Bearer ${player.token}` },
      });
      setUnlockedClues(res.data.data.data.cluesUnlocked);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setUnlockedClues([]);
      } else {
        console.error('Error fetching unlocked clues:', err);
      }
    }
  };
  // console.log('clues length:', clues.length);
  // console.log('Mapbox token:', process.env.REACT_APP_MAPBOX_TOKEN);
  // console.log('Hunt players:', hunt.players);
  // console.log('isJoined:', isJoined, 'Player', player.data.player._id);
  return (
    <div className="container mt-4">
      <HuntHeader title="JG Trailblazer Treasure Hunts" />

      {/* Join/Leave Buttons - Above the Fold */}
      <HuntJoinLeaveButton
        isJoined={isJoined}
        buttonLoading={buttonLoading}
        onJoin={handleJoin}
        onLeave={handleLeave}
        joinLabel="Join Treasure Hunt"
        leaveLabel="Leave Treasure Hunt"
        className="mb-4"
      />

      <h2 className="mb-3">{hunt.name}</h2>

      {/* Modern Card for Hunt Info */}
      <div
        className="card shadow-sm mb-4"
        style={{
          borderRadius: 18,
          overflow: 'hidden',
          background: 'linear-gradient(90deg, #f8fafc 60%, #e0e7ff 100%)',
          border: 'none',
        }}
      >
        {hunt.imageUrl && (
          <img
            src={hunt.imageUrl}
            alt={hunt.name}
            className="card-img-top"
            style={{
              maxHeight: 220,
              objectFit: 'cover',
              borderBottom: '1px solid #e5e7eb',
            }}
          />
        )}
        <div
          className="card-body"
          style={{
            background: 'rgba(30,41,59,0.85)', // dark blue with opacity
            color: '#fff',
            padding: '1.5rem 1.25rem',
          }}
        >
          <h2
            className="card-title mb-2"
            style={{
              fontWeight: 700,
              fontSize: 'clamp(1.3rem, 2vw, 2rem)',
              lineHeight: 1.2,
            }}
          >
            {hunt.name}
          </h2>
          <div
            className="d-flex flex-wrap align-items-center mb-2"
            style={{ gap: 12 }}
          >
            <span className="badge bg-primary" style={{ fontSize: 14 }}>
              {hunt.city}, {hunt.region}
            </span>
            <span className="badge bg-info" style={{ fontSize: 14 }}>
              {dayjs(hunt.startDate).format('MMM D')} -{' '}
              {dayjs(hunt.endDate).format('MMM D, YYYY')}
            </span>
            <span
              className="badge bg-warning text-dark"
              style={{ fontSize: 14 }}
            >
              Price: {hunt.priceInJGK} JGK
            </span>
          </div>
          <div className="d-flex justify-content-center">
            <BadgeList badges={hunt.badges} maxDisplay={3} />
          </div>{' '}
          {hunt.rewards && (
            <div className="mb-2">
              <span className="badge bg-success me-2">
                {hunt.rewards.xp ? `${hunt.rewards.xp} XP` : ''}
              </span>
              <span className="badge bg-warning text-dark me-2">
                {hunt.rewards.jgk ? `${hunt.rewards.jgk} JGK` : ''}
              </span>
              <span className="text-light" style={{ opacity: 0.8 }}>
                {hunt.rewards.description}
              </span>
            </div>
          )}
          {hunt.description && (
            <p
              className="card-text mt-2"
              style={{
                color: '#e0e7ef',
                fontSize: 'clamp(1rem, 1.2vw, 1.15rem)',
              }}
            >
              {hunt.description}
            </p>
          )}
          {hunt.players && hunt.players.length > 0 && (
            <PlayerAvatars players={hunt.players} maxDisplay={9} />
          )}
          <HuntJoinLeaveButton
            isJoined={isJoined}
            buttonLoading={buttonLoading}
            onJoin={handleJoin}
            onLeave={handleLeave}
            joinLabel="Join Treasure Hunt"
            leaveLabel="Leave Treasure Hunt"
            className="mb-4"
          />
        </div>
      </div>
      <HuntProgressDisplay huntId={huntId} />
      {/* 🗺️ Treasure Hunt Map */}
      <div className="container-fluid px-0" style={{ marginBottom: 32 }}>
        <div
          style={{
            width: '85vw',
            maxWidth: '1000px',
            margin: '0 auto',
            background: 'linear-gradient(120deg, #f1f5f9 60%, #e0e7ff 100%)',
            borderRadius: 24,
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
            overflow: 'hidden',
            border: '1px solid #e0e7ef',
            padding: '0',
            transition: 'box-shadow 0.2s',
          }}
        >
          <div
            style={{
              background: 'rgba(30,41,59,0.92)',
              color: '#fff',
              padding: '18px 32px 10px 32px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span style={{ fontSize: 28, marginRight: 8 }}>🗺️</span>
            <h5
              className="mb-0"
              style={{
                fontWeight: 700,
                fontSize: '1.25rem',
                letterSpacing: 0.5,
              }}
            >
              Treasure Hunt Map
            </h5>
            <span
              className="badge bg-secondary ms-auto"
              style={{ fontSize: 15, background: '#818cf8' }}
            >
              {clues?.length} clues
            </span>
          </div>
          <div ref={mapRef} style={{ width: '100%', padding: 0 }}>
            {clues?.length > 0 ? (
              <ReactMapGL
                initialViewState={viewport}
                viewState={viewport}
                style={{
                  width: '85vw',
                  height: 700,
                  borderRadius: 0,
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 400,
                  boxShadow: '0 4px 24px 0 rgba(31, 38, 135, 0.10)',
                  borderBottomLeftRadius: 24,
                  borderBottomRightRadius: 24,
                  transition: 'box-shadow 0.2s',
                }}
                padding={{ top: 40, bottom: 40, left: 40, right: 40 }}
                mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                mapStyle="mapbox://styles/joegalaxydotnet/clmquop4a04vw01phe5fi2sdq"
                onMove={(event) => setViewport(event.viewState)}
              >
                {/* Draw the circle */}
                {circleGeoJSON && (
                  <>
                    <Source
                      id="clue-circle"
                      type="geojson"
                      data={circleGeoJSON}
                    >
                      <Layer {...circleLayer} />
                      <Layer {...circleOutline} />
                    </Source>
                  </>
                )}
                {clues.map(
                  (clue, idx) =>
                    Array.isArray(clue.location.coordinates) &&
                    clue.location.coordinates.length === 2 && (
                      <Marker
                        key={clue._id || idx}
                        longitude={clue.location.coordinates[0]}
                        latitude={clue.location.coordinates[1]}
                        anchor="bottom"
                      >
                        <div
                          onClick={() => setSelectedClue(clue)}
                          style={{
                            fontSize: '2rem',
                            cursor: 'pointer',
                            filter: 'drop-shadow(0 2px 8px #0003)',
                            transition: 'transform 0.1s',
                            userSelect: 'none',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.85)',
                            padding: '2px 6px',
                            boxShadow: '0 2px 8px 0 rgba(31,38,135,0.10)',
                            border: `2.5px solid ${
                              unlockedClues.some((uc) => uc.clue === clue._id)
                                ? '#ef4444' // red for solved
                                : '#22c55e' // green for unsolved
                            }`,
                          }}
                          title="Show clue details"
                          role="button"
                          tabIndex={0}
                          aria-label="Show clue details"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                              setSelectedClue(clue);
                          }}
                        >
                          <span
                            style={{
                              color: unlockedClues.some(
                                (uc) => uc.clue === clue._id,
                              )
                                ? '#ef4444' // red for solved
                                : '#22c55e', // green for unsolved
                              transition: 'color 0.2s',
                            }}
                          >
                            📍
                          </span>
                        </div>
                      </Marker>
                    ),
                )}
                {selectedClue && (
                  <Popup
                    longitude={selectedClue.location.coordinates[0]}
                    latitude={selectedClue.location.coordinates[1]}
                    anchor="center"
                    onClose={() => setSelectedClue(null)}
                    closeOnClick={true}
                    maxWidth="350px"
                    closeButton={false}
                    focusAfterOpen={false}
                    style={{
                      borderRadius: 16,
                      boxShadow: '0 4px 24px 0 rgba(31,38,135,0.10)',
                      padding: 0,
                    }}
                  >
                    <ClueCard
                      clue={selectedClue}
                      huntName={hunt.name}
                      onClose={() => setSelectedClue(null)}
                      unlockedClues={unlockedClues}
                      fetchUnlockedClues={fetchUnlockedClues}
                    />
                  </Popup>
                )}
              </ReactMapGL>
            ) : (
              <div
                className="bg-light p-4 rounded text-muted text-center"
                style={{
                  fontSize: 18,
                  background: 'rgba(241,245,249,0.8)',
                  borderRadius: 16,
                  margin: 32,
                }}
              >
                No clues with map coordinates.
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modern Clues List */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: 18 }}>
        <div
          className="card-body"
          style={{
            background: 'rgba(30,41,59,0.92)',
            color: '#fff',
            borderRadius: 16,
          }}
        >
          <h5 className="mb-3" style={{ fontWeight: 600 }}>
            🔍 Clues
          </h5>
          {clues.length === 0 ? (
            <div className="text-muted">No clues available.</div>
          ) : (
            <ul className="list-group list-group-flush">
              {clues
                .slice() // create a copy so we don't mutate original
                .sort((a, b) => {
                  const aSolved = unlockedClues.some((uc) => uc.clue === a._id);
                  const bSolved = unlockedClues.some((uc) => uc.clue === b._id);
                  // Unsolved first, solved last
                  if (aSolved === bSolved) return 0;
                  return aSolved ? 1 : -1;
                })
                .map((clue, idx) => {
                  const isSolved = unlockedClues.some(
                    (uc) => uc.clue === clue._id,
                  );
                  const clueColor = isSolved ? '#fb923c' : '#22c55e'; // orange for solved, green for unsolved
                  const clueBg = isSolved
                    ? 'rgba(251,146,60,0.10)'
                    : 'rgba(34,197,94,0.10)'; // subtle background
                  return (
                    <li
                      key={clue._id || idx}
                      className="list-group-item"
                      style={{
                        border: 'none',
                        borderLeft: `8px solid ${clueColor}`,
                        borderBottom: '1px solid #334155',
                        padding: '18px 0',
                        background: clueBg,
                        color: '#fff',
                        transition: 'background 0.2s, border-left 0.2s',
                      }}
                    >
                      <div className="d-flex align-items-start gap-3 flex-column flex-md-row">
                        <span
                          style={{
                            fontSize: 22,
                            flexShrink: 0,
                            color: clueColor,
                          }}
                        >
                          {clue.type === 'qr' ? '🔳' : '📍'}
                        </span>
                        <div style={{ flex: 1 }}>
                          <strong style={{ color: clueColor }}>
                            {clue.type.toUpperCase()}:
                          </strong>{' '}
                          {clue.text}
                          {clue.imageUrl && (
                            <div className="mt-2">
                              <div
                                style={{
                                  width: '100%',
                                  maxWidth: 220,
                                  margin: '0 auto',
                                  borderRadius: 14,
                                  overflow: 'hidden',
                                  boxShadow: '0 4px 16px #0002',
                                  cursor: 'pointer',
                                  transition:
                                    'transform 0.15s, box-shadow 0.15s',
                                }}
                                onClick={() => {
                                  if (mapRef.current) {
                                    mapRef.current.scrollIntoView({
                                      behavior: 'smooth',
                                    });
                                  }
                                  setSelectedClue(clue);
                                  setViewport({
                                    ...viewport,
                                    latitude: clue.location.coordinates[1],
                                    longitude: clue.location.coordinates[0],
                                    zoom: 16,
                                  });
                                }}
                                title="View clue on map"
                                tabIndex={0}
                                role="button"
                                aria-label="View clue on map"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    if (mapRef.current) {
                                      mapRef.current.scrollIntoView({
                                        behavior: 'smooth',
                                      });
                                    }
                                    setSelectedClue(clue);
                                    setViewport({
                                      ...viewport,
                                      latitude: clue.location.coordinates[1],
                                      longitude: clue.location.coordinates[0],
                                      zoom: 16,
                                    });
                                  }
                                }}
                              >
                                <img
                                  src={clue.imageUrl}
                                  alt="Clue Visual"
                                  className="img-thumbnail"
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    borderRadius: 0,
                                    objectFit: 'cover',
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          {clue.type === 'riddle' && (
                            <div className="mt-2">
                              <button
                                className="btn btn-warning btn-sm w-100 w-md-auto"
                                onClick={() => {
                                  if (mapRef.current) {
                                    mapRef.current.scrollIntoView({
                                      behavior: 'smooth',
                                    });
                                  }
                                  setSelectedClue(clue);
                                  setViewport({
                                    ...viewport,
                                    latitude: clue.location.coordinates[1],
                                    longitude: clue.location.coordinates[0],
                                    zoom: 16,
                                  });
                                }}
                              >
                                View Riddle Location
                              </button>
                            </div>
                          )}
                          {/* Button on new line after clue.text */}
                          {clue.type === 'location' && (
                            <div className="mt-2">
                              <button
                                className="btn btn-primary btn-sm w-100 w-md-auto"
                                onClick={() => {
                                  if (mapRef.current) {
                                    mapRef.current.scrollIntoView({
                                      behavior: 'smooth',
                                    });
                                  }
                                  setSelectedClue(clue); // Open ClueCard popup for this clue
                                  setViewport({
                                    ...viewport,
                                    latitude: clue.location.coordinates[1],
                                    longitude: clue.location.coordinates[0],
                                    zoom: 16, // Zoom in for effect
                                  });
                                }}
                              >
                                Unlock via GPS
                              </button>
                            </div>
                          )}
                          {clue.type === 'qr' && (
                            <div className="mt-2">
                              <button
                                className="btn btn-qr-scan btn-sm w-100 w-md-auto"
                                onClick={() => {
                                  if (mapRef.current) {
                                    mapRef.current.scrollIntoView({
                                      behavior: 'smooth',
                                    });
                                  }
                                  setSelectedClue(clue);
                                  setViewport({
                                    ...viewport,
                                    latitude: clue.location.coordinates[1],
                                    longitude: clue.location.coordinates[0],
                                    zoom: 16,
                                  });
                                }}
                              >
                                <span
                                  role="img"
                                  aria-label="qr"
                                  style={{ marginRight: 6, fontSize: 18 }}
                                >
                                  🔳
                                </span>
                                <span>Scan QR</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </div>
      {/* 📊 Player Progress */}
      <div className="row g-3 mb-4">
        {/* <div className="col-md-6">
          <div
            className="card shadow-sm"
            style={{
              borderRadius: 18,
              background: 'rgba(30,41,59,0.92)',
              color: '#fff',
            }}
          >
            <div className="card-body">
              <h5 className="mb-2" style={{ fontWeight: 600 }}>
                📊 Your Progress
              </h5>
              <div className="bg-light p-3 rounded text-muted">
                Completed {0}/{clues.length} clues
              </div>
            </div>
          </div>
        </div> */}
        <div className="col-md-6">
          <div
            className="card shadow-sm"
            style={{
              borderRadius: 18,
              background: 'rgba(30,41,59,0.92)',
              color: '#fff',
            }}
          >
            <div className="card-body">
              <h5 className="mb-2" style={{ fontWeight: 600 }}>
                📍 Check-In
              </h5>
              <div className="bg-light p-3 rounded text-muted">
                [Check-In functionality coming soon]
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add space above and below the Join/Leave buttons */}
      <div style={{ height: 32 }}></div>
      <HuntJoinLeaveButton
        isJoined={isJoined}
        buttonLoading={buttonLoading}
        onJoin={handleJoin}
        onLeave={handleLeave}
        joinLabel="Join Treasure Hunt"
        leaveLabel="Leave Treasure Hunt"
        className="mb-4"
      />
      <div style={{ height: 32 }}></div>

      {/* Join/Leave Buttons - Bottom */}
      {/* <div className="mb-5 d-flex justify-content-center">
        <button className="animated-subscribe-btn btn btn-success me-2">
          Join Treasure Hunt
        </button>
        <button className="btn btn-outline-danger">Leave Treasure Hunt</button>
      </div> */}
    </div>
  );
};

export default HuntDetailPage;
